import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { API_URL } from "@/components/config/config";
import { AppStateContext } from "@/components/context/AppContext";
import fetchWithAuth from "@/hooks/fetchWithAuth";
import { clearProfileCache } from "@/hooks/useFetchUserDetails";

import { PSEUDO, languagesEnabled, setUiLanguage, storedLanguage } from "./index";

// The account's language (Profile.ui_language) wins once the profile loads, so
// the choice follows the recruiter to every device; localStorage only covers
// the moments before that. An account with no language yet takes the one this
// browser chose before sign-in (a native page's ?lang= link or the switcher),
// which is how a sign-up from /de lands in a German account. Only the portal
// layout passes sync=true, so this runs once per page, not once per caller.
export function usePortalLanguage(sync = false) {
  const { i18n } = useTranslation();
  const { userProfile, setUserProfile } = useContext(AppStateContext);
  const saved = userProfile?.ui_language;
  const uid = userProfile?.uid;

  async function save(code: string) {
    if (!uid || code === PSEUDO || code === saved) return;
    const response = await fetchWithAuth(`${API_URL}/profiles/${uid}/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ui_language: code }),
    });
    if (response.ok) {
      clearProfileCache();
      setUserProfile({ ...userProfile, ui_language: code });
    }
  }

  useEffect(() => {
    if (!sync || !languagesEnabled() || !uid) return;
    if (saved) {
      if (saved !== i18n.language && i18n.language !== PSEUDO) void setUiLanguage(saved);
      return;
    }
    const local = storedLanguage();
    if (local && local !== "en" && local !== PSEUDO) void save(local);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, uid]);

  async function choose(code: string) {
    await setUiLanguage(code);
    await save(code);
  }

  return { language: i18n.language, choose };
}
